import { useState } from "react";
import { Link } from "zudoku/components";
import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  MessageSquareTextIcon,
} from "zudoku/icons";
import { Button } from "zudoku/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "zudoku/ui/Card";
import skills from "../generated/skills.json";
import { McpServerChips, McpServerList } from "./mcpServers";

/**
 * The Skills Library and skill pages render entirely from the manifest that
 * scripts/build-skills.mjs generates from the folders in `skills/`, so adding a
 * skill needs no change here.
 */
const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// "2026-10-13" as "Oct 13, 2026", read as a calendar date so the time zone of
// the build or the browser can't shift it by a day.
const formatDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const versionLabel = (skill: {
  version: string | null;
  updated: string | null;
}) =>
  [
    skill.version && `Version ${skill.version}`,
    skill.updated && `Updated ${formatDate(skill.updated)}`,
  ]
    .filter(Boolean)
    .join(" · ");

// The whole card links to the skill's page through the title link, stretched
// over the card, so the download link can sit inside the card without nesting
// one link in another.
export const SkillsLibrary = () => (
  <div className="not-prose my-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
    {skills.map((skill) => (
      <Card
        key={skill.name}
        className="relative h-full hover:ring-primary transition-colors"
      >
        <CardHeader>
          <CardTitle className="text-base font-medium">
            <Link
              to={`/mcp/skills/${skill.name}`}
              className="after:absolute after:inset-0 after:rounded-[inherit]"
            >
              {skill.title}
            </Link>
          </CardTitle>
          <CardDescription className="line-clamp-4">
            {skill.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="mt-auto flex items-center justify-between gap-3">
          <McpServerList
            servers={skill.mcpServers}
            optional={skill.mcpServersOptional}
          />
          <div className="flex items-center gap-1.5">
            {skill.version && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                v{skill.version}
              </span>
            )}
            <a
              href={skill.download}
              download={`${skill.name}.zip`}
              aria-label={`Download ${skill.title}`}
              title="Download skill"
              className="relative z-10 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <DownloadIcon size={14} aria-hidden="true" />
            </a>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

const findSkill = (name: string) => skills.find((skill) => skill.name === name);

/** The description and download button at the top of a skill's page. */
export const SkillOverview = ({ name }: { name: string }) => {
  const skill = findSkill(name);
  if (!skill) return null;

  const fileName = `${skill.name}.zip`;

  return (
    <>
      {skill.details.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      <div className="not-prose flex flex-wrap items-center gap-3 my-4">
        <Button asChild size="lg" className="h-11 gap-2 px-5 text-base">
          <a href={skill.download} download={fileName}>
            <DownloadIcon size={18} aria-hidden="true" />
            Download skill
          </a>
        </Button>
        <span className="text-sm text-muted-foreground">
          {[fileName, formatSize(skill.downloadSize), versionLabel(skill)]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </>
  );
};

/** The MCP servers a skill uses, on its page. */
export const SkillServers = ({ name }: { name: string }) => {
  const skill = findSkill(name);
  if (!skill) return null;

  return (
    <McpServerChips
      servers={skill.mcpServers}
      optional={skill.mcpServersOptional}
    />
  );
};

const PromptRow = ({ prompt }: { prompt: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <li className="group flex items-center gap-3 text-sm">
      <MessageSquareTextIcon
        size={14}
        aria-hidden="true"
        className="shrink-0 text-muted-foreground"
      />
      <span className="flex-1">{prompt}</span>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy prompt"}
        title={copied ? "Copied" : "Copy prompt"}
        className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 hover:text-accent-foreground"
      >
        {copied ? (
          <CheckIcon size={14} aria-hidden="true" />
        ) : (
          <CopyIcon size={14} aria-hidden="true" />
        )}
      </button>
    </li>
  );
};

/** Example prompts for a skill, each with its own copy button. */
export const SkillPrompts = ({ name }: { name: string }) => {
  const skill = findSkill(name);
  if (!skill) return null;

  return (
    <ul className="not-prose my-4 space-y-3">
      {skill.examplePrompts.map((prompt) => (
        <PromptRow key={prompt} prompt={prompt} />
      ))}
    </ul>
  );
};
